/**
 * localStorage/sessionStorage, которые не бросают исключений.
 *
 * Обращение к самому свойству `window.localStorage` — не безобидное чтение поля: браузер может
 * ответить отказом. Chrome с заблокированными данными сайта отдаёт
 * «SecurityError: Failed to read the 'localStorage' property from 'Window': Access is denied
 * for this document», Safari в приватном режиме отдаёт объект, но падает уже на setItem.
 * Проверка `typeof localStorage === "undefined"` от этого не спасает: она сама дёргает геттер
 * и падает вместе с ним.
 *
 * В приложении хранилище читается из рендера (список сохранённых авто), поэтому такой отказ
 * ронял не отдельную функцию, а весь экран: исключение улетало в границу ошибок и вместо
 * страховки пользователь видел «Что-то пошло не так».
 *
 * Здесь хранилище проверяется один раз пробной записью, а при отказе подменяется хранилищем
 * в памяти. Приложение продолжает работать в пределах вкладки; переживать перезагрузку данные
 * перестают — но это несравнимо лучше пустого экрана.
 */

export type StorageLike = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
  key: (index: number) => string | null;
  readonly length: number;
};

const PROBE_KEY = "__simble_storage_probe__";

const createMemoryStorage = (): StorageLike => {
  const map = new Map<string, string>();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    },
    clear: () => map.clear(),
    key: (index) => [...map.keys()][index] ?? null,
    get length() {
      return map.size;
    },
  };
};

/** Пробная запись: только она отличает «хранилище есть» от «объект есть, а писать нельзя». */
const probeStorage = (read: () => Storage): Storage | null => {
  try {
    const storage = read();
    storage.setItem(PROBE_KEY, PROBE_KEY);
    storage.removeItem(PROBE_KEY);
    return storage;
  } catch {
    return null;
  }
};

/**
 * Даже прошедшее пробу хранилище может отказать позже: квота кончается на конкретной записи,
 * а приватный режим Firefox снимает доступ при смене настроек. Поэтому каждый вызов тоже под catch.
 */
const hardenStorage = (storage: Storage): StorageLike => ({
  getItem: (key) => {
    try {
      return storage.getItem(key);
    } catch {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      storage.setItem(key, value);
    } catch {
      // Переполнена квота или доступ отозван: терять запись здесь безопаснее, чем падать.
    }
  },
  removeItem: (key) => {
    try {
      storage.removeItem(key);
    } catch {
      // см. setItem
    }
  },
  clear: () => {
    try {
      storage.clear();
    } catch {
      // см. setItem
    }
  },
  key: (index) => {
    try {
      return storage.key(index);
    } catch {
      return null;
    }
  },
  get length() {
    try {
      return storage.length;
    } catch {
      return 0;
    }
  },
});

const createSafeStorage = (read: () => Storage): { storage: StorageLike; isPersistent: boolean } => {
  const native = typeof window === "undefined" ? null : probeStorage(read);
  return native ? { storage: hardenStorage(native), isPersistent: true } : { storage: createMemoryStorage(), isPersistent: false };
};

const local = createSafeStorage(() => window.localStorage);
const session = createSafeStorage(() => window.sessionStorage);

export const safeLocalStorage: StorageLike = local.storage;
export const safeSessionStorage: StorageLike = session.storage;

/** false — данные живут только в памяти вкладки: браузер запретил хранилище. */
export const isLocalStoragePersistent = (): boolean => local.isPersistent;
export const isSessionStoragePersistent = (): boolean => session.isPersistent;
